import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourParentsBox from '../../components/harbour/HarbourParentsBox';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourStageStrip from '../../components/harbour/HarbourStageStrip';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourToast from '../../components/harbour/HarbourToast';
import HarbourTickRow from '../../components/harbour/HarbourTickRow';
import HarbourModal from '../../components/harbour/HarbourModal';
import HarbourOptionGroup from '../../components/harbour/HarbourOptionGroup';
import HarbourTeachersBox from '../../components/harbour/HarbourTeachersBox';
import HarbourAddTeacherModal from '../../components/harbour/HarbourAddTeacherModal';
import HarbourTeacherFormsModal from '../../components/harbour/HarbourTeacherFormsModal';
import HarbourChangeTeacherModal from '../../components/harbour/HarbourChangeTeacherModal';
import HarbourChangeParentModal from '../../components/harbour/HarbourChangeParentModal';
import HarbourIcon, { HarbourGlyph } from '../../components/harbour/HarbourIcon';
import HarbourReferralBlock from '../../components/harbour/HarbourReferralBlock';
import {
  formatLongDate, isSenPathwayEligible, isStoppedPathwayState, letterSentAt, letterSharedWithRole,
  pathwayGuardian, referralBoardStage, roleFormChip, senPlanStatus, viewLetterLabel, REFERRAL_STAGE_LABELS,
} from '../../utils/harbourPupils';
import { downloadTriageLetter } from '../../utils/triageLetterDownload';
import {
  isReferralWithdrawn,
  isRoleSubmissionConfirmed,
  recordRequiresSubmissionConfirmation,
  senFormsProgress,
} from '../../utils/referralStatus';
import { FAMILY_SERVICE_NAME, SERVICE_REFERRALS_CONTACT } from '../../utils/familyPortal';
import { PATHWAY_LABELS } from '../../utils/triagePathways';
import {
  apiHarbourInviteFamily, apiResendParticipantInvite, apiHarbourSetSenPlanAction,
  apiHarbourRemoveTeacher, apiHarbourStopSenPathway,
} from '../../utils/rmsApi';
import { harbourFormKeysFor, schoolFormsByPerson } from '../../utils/harbourForms';
import { formsBackLine, teacherFormGroups, teacherFullName } from '../../utils/schoolTeachers';
import useOrgHfpInstrument from '../../hooks/useOrgHfpInstrument';
import useMyReferralDocuments, { useDocumentRemoval } from '../../hooks/useMyReferralDocuments';
import HarbourDocumentsBlock from '../../components/harbour/HarbourDocumentsBlock';
import { REMOVE_CONFIRM, REMOVE_TITLE, removeBody } from '../../utils/referralDocumentPortal';
import '../../components/harbour/harbour.css';

/*
 * Harbour pupil record flyout (TDI-53 sections 03, 12, 14, 18, 20, 21).
 * Opens over Your pupils at /rms/pupils/:patientRecordId. Shows the parents
 * box and one section per pathway; each section is either active (stage
 * strip + work rows) or the quiet "Not active" state with its add action.
 */
export default function HarbourPupilRecordFlyout({ pupil, orgId, loading, notFound, onClose, onRefresh }) {
  // The organisation's HFP instrument setting (TDI-523): the form set of a
  // pupil whose profile has not started; a started profile has its own pin.
  const orgHfpInstrument = useOrgHfpInstrument(orgId);
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  // The Download menu closes like any menu: Escape, or a click anywhere else.
  const downloadMenuRef = useRef(null);
  useEffect(() => {
    if (!downloadMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDownloadMenuOpen(false);
    };
    const onPointerDown = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [downloadMenuOpen]);
  const [addTeacherOpen, setAddTeacherOpen] = useState(false);
  // Teacher delegation (TDI-489, TDI-618): whose forms are being chosen,
  // whose details are being changed (TDI-617), which teacher a removal is
  // confirming, and which row's email is on its way.
  const [allocatingTeacher, setAllocatingTeacher] = useState(null);
  const [changingTeacher, setChangingTeacher] = useState(null);
  const [removingTeacher, setRemovingTeacher] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  // "Change parent or carer" (TDI-622): whose details are being corrected.
  const [changingParent, setChangingParent] = useState(null);

  // The success notice arrives once in router state. Keep it for this visit,
  // then clear it from history so a reload does not announce it again.
  const [justAdded] = useState(() => (location.state && location.state.harbourAlert) || null);
  // A document just added from the Add a document flyout (TDI-578), or one
  // just removed here: the quiet confirmation at the top of the record (D-04
  // frames 03 and 06), dismissible.
  const [documentNotice, setDocumentNotice] = useState(() => (
    location.state && location.state.documentNotice
      ? { kind: 'info', title: location.state.documentNotice }
      : null
  ));
  const alertInHistory = Boolean(location.state && (location.state.harbourAlert || location.state.documentNotice));
  useEffect(() => {
    if (alertInHistory) {
      navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: null });
    }
  }, [alertInHistory, navigate, location.pathname, location.search]);

  // A pupil id that is not on this school's list never arrives, so waiting for
  // it is waiting for nothing: the skeleton used to spin for good on a stale
  // bookmark, a pupil who moved school, or a mistyped id. Say so, and offer the
  // way back.
  if (notFound && !pupil) {
    return (
      <HarbourFlyout width="record" title="Pupil not found" onClose={onClose}>
        <div className="flex flex-col items-start gap-5">
          <p className="harbour-body-16">
            We could not find this pupil on your list. They may have been removed,
            or the link you followed may be out of date.
          </p>
          <HarbourButton kind="primary" onClick={onClose}>Back to your pupils</HarbourButton>
        </div>
      </HarbourFlyout>
    );
  }

  // Until the record's first snapshot lands, the pupil is built from the list
  // row alone — no year group, no pathways — and rendering that showed a cold
  // load "cannot be added to a pathway yet" for a real Year 6 with a referral
  // (TDI-807). statusUnknown with recordSettled means the listener failed, so
  // that gets the portal's load error rather than loading for ever.
  if (loading || !pupil || (pupil.statusUnknown && !pupil.recordSettled)) {
    return (
      <HarbourFlyout width="record" title="Loading pupil record" onClose={onClose}>
        <div aria-hidden="true" className="flex flex-col gap-5">
          <div className="harbour-skeleton h-[72px] animate-pulse" />
          <div className="harbour-skeleton h-[220px] animate-pulse" />
          <div className="harbour-skeleton h-[140px] animate-pulse" />
        </div>
      </HarbourFlyout>
    );
  }

  if (pupil.statusUnknown && pupil.recordSettled) {
    return (
      <HarbourFlyout width="record" title={pupil.name || 'Pupil record'} onClose={onClose}>
        <div className="flex flex-col items-start gap-4">
          <HarbourNotice kind="error" title="We could not load this page right now">
            Check your connection, then try again.
          </HarbourNotice>
          {onRefresh && (
            <HarbourButton kind="secondary" onClick={() => onRefresh()}>Try again</HarbourButton>
          )}
        </div>
      </HarbourFlyout>
    );
  }

  const firstName = pupil.name.split(' ')[0];
  // Exactly one parent or carer is the nominated one. The per-participant
  // isInformant flag is only ever set, never cleared, so a record that has
  // been re-nominated carries two of them; resolve a single id first and
  // compare against that, preferring the record's own nomination.
  const guardianList = pupil.guardians || [];
  const informantId =
    pupil.informantParticipantId
    || ((guardianList.find((g) => g.isInformant) || {}).participantId)
    || ((guardianList.find((g) => g.isInformant) || {}).id)
    || null;
  const parents = guardianList.map((g) => ({
    participantId: g.participantId || g.id,
    firstName: g.firstName,
    lastName: g.lastName,
    email: g.email,
    phone: g.phone,
    relationship: g.relationship,
    // Whether they have signed in, and whether the invitation has gone,
    // decide what "Change parent or carer" lets the SENCO change (TDI-622).
    status: g.status,
    invitation: g.invitation || null,
    isInformant: Boolean(informantId) && (g.participantId || g.id) === informantId,
  }));
  // Each section names and sends to its own parent or carer: SEND the
  // primary parent (entered with the pupil), referral the nominated one.
  const senGuardian = pathwayGuardian(pupil, 'sen');
  const referralGuardian = pathwayGuardian(pupil, 'referral');
  // The SEND pathway is Key Stage 1 and 2 only (TDI-488): Reception and
  // secondary pupils are not offered it.
  const senOffered = isSenPathwayEligible(pupil);

  const sen = pupil.sen;
  const senStopped = sen && sen.stage === 'stopped';
  // The family took their permission back (TDI-479, E-7 and E-8): the record
  // keeps what was done, read-only. Before the profile the forms stay
  // readable; after it the profile stays, and the plan ticks are off.
  const senWithdrawn = Boolean(senStopped && sen.stopReason === 'consent_withdrawn');
  const referral = pupil.referral;
  // The referral has left the school once any pathway has moved past being
  // filled in: from then on the forms are locked and the record stops
  // offering them.
  const referralSent = Boolean(referral)
    && (referral.pathways || []).some((p) => p.status !== 'inProgress');
  // The letter opens only once triage is decided AND the clinician has shared
  // it with school: the same rule the server applies before serving the file.
  const letterRows = ((referral && referral.pathways) || []).filter(
    (p) => (p.status === 'outcomeAvailable' || p.status === 'closed')
      && letterSharedWithRole(pupil.patient, p.key, 'school_user')
  );
  const isHarbourPupil = Boolean(pupil.patient && pupil.patient.harbour && pupil.patient.harbour.pupil);
  // One filled button per view (frames 4223:94139 / 94627 / 95193): the SEND
  // section owns it while it offers "Add to SEND pathway" or unfinished school
  // forms; otherwise it passes to the referral section.
  const senAssessing = Boolean(sen && !senStopped && !sen.reportAvailable);
  // Finishing the forms is not the last thing the school has to do: on a record
  // gated on confirm-and-submit (TDI-418) the profile waits on each respondent
  // confirming. That step lives inside the forms flyout, so the record has to
  // name it — "created when both sets of forms are done" was already true here
  // and nothing was happening.
  const senNeedsConfirmStep = recordRequiresSubmissionConfirmation(pupil.patient);
  // The SEND pathway's own confirmations (TDI-651): the referral's never
  // stand in for them.
  const senSchoolToConfirm = senAssessing && senNeedsConfirmStep
    && pupil.senSchoolStatus === 'completed'
    && !isRoleSubmissionConfirmed(pupil.patient, 'school_user', 'sen');
  const senGuardianConfirmed = senNeedsConfirmStep
    && isRoleSubmissionConfirmed(pupil.patient, 'guardian', 'sen');
  const senGuardianToConfirm = senAssessing && senNeedsConfirmStep
    && pupil.senGuardianStatus === 'completed'
    && !senGuardianConfirmed;
  const senOwnsFilled = (!sen && senOffered)
    || (senAssessing && pupil.senSchoolStatus !== 'completed')
    || senSchoolToConfirm;
  // The referral waits on the same final step (TDI-418, per pathway since
  // TDI-651): it is sent once the school and the parent or carer have each
  // confirmed. Without saying so, a record with every form "Done" just sat
  // at In progress (staging, Montgomery Academy, 2 Nov).
  const referralConfirmOpen = Boolean(referral) && !referralSent
    && recordRequiresSubmissionConfirmation(pupil.patient) && !isReferralWithdrawn(pupil.patient);
  const referralSchoolToConfirm = referralConfirmOpen
    && pupil.schoolStatus === 'completed'
    && !isRoleSubmissionConfirmed(pupil.patient, 'school_user', 'referral');
  const referralGuardianConfirmed = referralConfirmOpen
    && isRoleSubmissionConfirmed(pupil.patient, 'guardian', 'referral');
  const referralGuardianToConfirm = referralConfirmOpen
    && pupil.guardianStatus === 'completed'
    && !referralGuardianConfirmed;
  // While SEND is at Assess the referral add stays quiet, as drawn (94627).
  const referralAddKind = (!sen && senOffered) || senAssessing ? 'secondary' : 'primary';
  const guardianFirstName = senGuardian && senGuardian.firstName;
  const senProgress = (role) => senFormsProgress(role, pupil.formProgress, pupil.patient, orgHfpInstrument);

  // Plan-stage steps ticked off on the record once the profile report exists
  // (Figma 4486:37044, PROPOSED, Tom's 19 Oct sweep): a tick records at once
  // with no confirm pop-up, can be unticked, and the meeting row shows the
  // date it was ticked. Recorded server-side with who and when.
  const senPlan = (pupil.patient && pupil.patient.senPathway && pupil.patient.senPathway.plan) || {};
  // Plan, then Do, then Review, in order (rmsHarbourSetSenPlanAction): each
  // step appears once the one before it is ticked. The action keys are the
  // server contract and must not change with the names. Do and Review wording
  // is interim, pending Tom and Jasmine.
  const SEN_PLAN_STEPS = [
    { action: 'meetingScheduled', label: 'Schedule meeting with family' },
    { action: 'planCreated', label: 'Create individual support plan' },
    { action: 'supportInPlace', label: 'Put the support in place', after: 'planCreated' },
    { action: 'reviewHeld', label: 'Review the support with the family', after: 'supportInPlace' },
  ].filter((step) => !step.after || senPlan[step.after]);
  const planStatus = senPlanStatus(pupil.patient);
  // Teachers on the record, with their form allocations (TDI-489).
  const teachers = (pupil.teachers || []);
  // The school forms a teacher can be handed (TDI-618): of the record's own
  // set, Education history and the ADHD symptom scale on the referral and
  // the strengths and needs forms on the SEND pathway, grouped by pathway.
  const teacherGroups = teacherFormGroups({
    referralFormKeys: referral
      ? harbourFormKeysFor('referral', {
          patient: pupil.patient || null,
          intakeConcern: pupil.patient?.intakeConcern || null,
          senActive: Boolean(sen && sen.stage !== 'stopped'),
          schoolPhase: pupil.patient?.schoolPhase || null,
        })
      : [],
    senFormKeys: sen && sen.stage !== 'stopped'
      ? harbourFormKeysFor('sen', {
          patient: pupil.patient || null,
          intakeConcern: pupil.patient?.intakeConcern || null,
          senActive: true,
          schoolPhase: pupil.patient?.schoolPhase || null,
          orgSetting: orgHfpInstrument,
        })
      : [],
  });
  const teacherByKey = (key) => teachers.find((t) => (t.participantId || t.id || t.email) === key) || null;
  const recordSchoolName = (pupil.patient && pupil.patient.school
    && (pupil.patient.school.schoolName || pupil.patient.school.name)) || null;
  // Who holds which of the SEND school forms, and how far each has got.
  const senSchoolByPerson = schoolFormsByPerson({
    formKeys: sen && sen.stage !== 'stopped'
      ? harbourFormKeysFor('sen', {
          patient: pupil.patient || null,
          intakeConcern: pupil.patient?.intakeConcern || null,
          senActive: true,
          schoolPhase: pupil.patient?.schoolPhase || null,
        })
      : [],
    formProgress: pupil.formProgress,
    teachers,
  });
  const referralSchoolByPerson = schoolFormsByPerson({
    formKeys: referral
      ? harbourFormKeysFor('referral', {
          patient: pupil.patient || null,
          intakeConcern: pupil.patient?.intakeConcern || null,
          senActive: Boolean(sen && sen.stage !== 'stopped'),
          schoolPhase: pupil.patient?.schoolPhase || null,
        })
      : [],
    formProgress: pupil.formProgress,
    teachers,
  });
  // The teacher row's one action (TDI-618): the first email again until they
  // have a password, then a reminder at most once in 24 hours. The server
  // decides which, from the same facts the row shows.
  const resendTeacher = async (t) => {
    const id = t.participantId || t.id;
    const first = t.firstName || 'the teacher';
    setResendingId(id);
    setActionError(null);
    try {
      const res = await apiResendParticipantInvite({ orgId, patientRecordId: pupil.id, participantId: id });
      setToast({
        title: res && res.kind === 'reminder' ? `We've sent ${first} a reminder.` : `We've emailed ${first} again.`,
      });
      if (onRefresh) onRefresh();
    } catch (e) {
      setActionError(e?.message || 'The email could not be sent. Check your connection and try again.');
    } finally {
      setResendingId(null);
    }
  };
  const formsSaved = (res) => {
    const first = (allocatingTeacher && allocatingTeacher.firstName) || 'the teacher';
    setAllocatingTeacher(null);
    const gave = Boolean(res && res.added && res.added.length);
    let body = null;
    if (gave && res.emailed) body = `We've emailed ${first} to let them know which forms are theirs.`;
    else if (gave) body = `We could not email ${first}. Use "Send the email again" on their row.`;
    setToast({ title: 'Forms saved.', body });
    if (onRefresh) onRefresh();
  };
  const removeTeacher = async () => {
    setRemoveBusy(true);
    try {
      await apiHarbourRemoveTeacher({
        orgId,
        patientRecordId: pupil.id,
        participantId: removingTeacher.participantId || removingTeacher.id,
      });
      setRemovingTeacher(null);
      setToast({ title: 'Teacher removed', body: `They no longer have access to ${firstName}'s forms.` });
      if (onRefresh) await onRefresh();
    } catch (e) {
      setRemovingTeacher(null);
      setActionError(e?.message || 'The teacher could not be removed.');
    } finally {
      setRemoveBusy(false);
    }
  };
  // A referral's letter saved from its row (TDI-691): the same guarded fetch
  // as the letter page's Download, so the server decides who may have it.
  const downloadLetter = async (pathwayKey) => {
    setBusyAction(`download:${pathwayKey}`);
    setActionError(null);
    try {
      await downloadTriageLetter({ orgId, patientRecordId: pupil.id, pathway: pathwayKey });
    } catch (_e) {
      setActionError('The letter could not be downloaded. Check your connection and try again.');
    } finally {
      setBusyAction(null);
    }
  };
  // The downloads open as print views in their own tab: the browser's save
  // as PDF is version one of the pipeline, real output over a dead control.
  const openPrintView = (route) => {
    setDownloadMenuOpen(false);
    window.open(route, '_blank', 'noopener');
  };
  // A teacher chosen from the school's list, or added to it, is on the
  // record now (TDI-616): go straight on to giving them forms. Closing that
  // without ticking anything sends no email.
  const teacherAdded = (res) => {
    setAddTeacherOpen(false);
    const teacher = (res && res.teacher) || {};
    // A teacher already on this pupil opens with the forms they hold.
    const listed = res && res.alreadyListed
      ? teachers.find((t) => (t.participantId || t.id) === res.participantId)
      : null;
    if (listed) {
      setAllocatingTeacher(listed);
      return;
    }
    setAllocatingTeacher({
      participantId: res && res.participantId,
      schoolTeacherId: res && res.schoolTeacherId,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      jobRole: teacher.jobRole,
      allocations: {},
    });
    if (onRefresh) onRefresh();
  };
  // "Add a teacher" from a pathway's School forms block (TDI-616): the same
  // search as the Teacher box.
  const addTeacherRow = (
    <div className="harbour-record-row harbour-record-row--sub">
      <HarbourTextLink leadingIcon="plus" onClick={() => setAddTeacherOpen(true)}>
        Add a teacher
      </HarbourTextLink>
    </div>
  );

  // Stop the SEND pathway from the school side (TDI-805, ruling R-056): the
  // consent statement tells families they can leave "in the Digital Hub or
  // by telling the school's SENCO", so the SENCO needs the action. The
  // server stops the pathway, ends clinician sight of the profile and — for
  // "The family has told us they want to leave" — sends the same
  // consent-change emails as the family's own withdrawal.
  const [stopSenOpen, setStopSenOpen] = useState(false);
  const [stopSenReason, setStopSenReason] = useState(null);
  const [stopSenError, setStopSenError] = useState(null);
  const confirmStopSen = async () => {
    if (!stopSenReason) {
      setStopSenError('Choose why the pathway is stopping');
      return;
    }
    setBusyAction('stopSen');
    try {
      await apiHarbourStopSenPathway({ orgId, patientRecordId: pupil.id, reason: stopSenReason });
      // The live record listener flips the pathway to stopped everywhere.
      setStopSenOpen(false);
      setToast({ title: 'SEND pathway stopped',
                 body: `The family can give their permission again in the Digital Hub, and the pathway will carry on from where it stopped.` });
    } catch (e) {
      setStopSenError(e?.message || 'The pathway could not be stopped. Check your connection and try again.');
    } finally {
      setBusyAction(null);
    }
  };

  const togglePlanAction = async (step, ticked) => {
    setBusyAction('planAction');
    setActionError(null);
    try {
      await apiHarbourSetSenPlanAction({ orgId, patientRecordId: pupil.id, action: step.action, ticked });
      // The live record listener flips the tick and the status chip.
    } catch (e) {
      setActionError(e?.message || 'The step could not be recorded. Check your connection and try again.');
    } finally {
      setBusyAction(null);
    }
  };

  const inviteFamily = async (guardian) => {
    setBusyAction('invite');
    setActionError(null);
    try {
      if (guardian && guardian.status === 'invite_sent') {
        await apiResendParticipantInvite({
          orgId,
          patientRecordId: pupil.id,
          participantId: guardian.participantId || guardian.id,
        });
        setToast({ title: 'Reminder sent', body: `We have emailed ${guardian.firstName || 'the parent or carer'} again with their sign-in link.` });
      } else {
        await apiHarbourInviteFamily({
          orgId,
          patientRecordId: pupil.id,
          participantId: guardian ? (guardian.participantId || guardian.id) : undefined,
        });
        setToast({ title: 'Sign-in link sent', body: `We have emailed ${guardian?.firstName || 'the parent or carer'} a link to their forms.` });
      }
      if (onRefresh) onRefresh();
    } catch (e) {
      setActionError(e?.message || 'The invitation could not be sent. Check your connection and try again.');
    } finally {
      setBusyAction(null);
    }
  };

  // Parent-forms action for a lane: send the link (never sent yet), or a
  // gentle reminder (sent, not yet started). Nothing once they are under way.
  const parentFormsAction = (parentStatus, guardian) => {
    if (!guardian || parentStatus === 'completed') return null;
    // One reminder a week for a family, the platform's own included (email
    // spec 4993:2, frame 5331:58): until the week is up the row says when
    // the last went and when the next may, in place of the button.
    const lastSent = toReminderDate(guardian.lastReminderAt);
    const nextAllowed = lastSent ? new Date(lastSent.getTime() + FAMILY_REMINDER_INTERVAL_MS) : null;
    if (nextAllowed && nextAllowed > new Date()) {
      return (
        <span className="harbour-record-row__meta">
          {`Reminder sent on ${formatTickDate({ at: lastSent })}. You can send another from ${formatTickDate({ at: nextAllowed })}.`}
        </span>
      );
    }
    const pending = guardian.status === 'nominated' || guardian.status === 'invite_sent';
    if (!pending) return null;
    const label = guardian.status === 'nominated' ? 'Send link' : 'Send reminder';
    if (parentStatus === 'started') return null;
    // The platform reminds families on its own now (TDI-485), so say when it
    // last did: a SENCO about to chase should know the family has already
    // been nudged, and not feel they have to do it all themselves.
    const lastReminded = formatTickDate({ at: guardian.lastReminderAt });
    return (
      <span className="flex items-center gap-3">
        {lastReminded && (
          <span className="harbour-record-row__meta">{`Last reminded ${lastReminded}`}</span>
        )}
        <HarbourButton kind="secondary" size="small" busy={busyAction === 'invite'} onClick={() => inviteFamily(guardian)}>
          {label}
        </HarbourButton>
      </span>
    );
  };

  return (
    <HarbourFlyout width="record" title={pupil.name} year={pupil.yearGroup} onClose={onClose}>
      {justAdded && (
        <HarbourNotice kind="success" title={justAdded}>
          {null}
        </HarbourNotice>
      )}
      {actionError && (
        <HarbourNotice kind="error" title="That did not work" onClose={() => setActionError(null)}>
          {actionError}
        </HarbourNotice>
      )}
      {documentNotice && (
        <HarbourNotice kind={documentNotice.kind} title={documentNotice.title} onClose={() => setDocumentNotice(null)}>
          {documentNotice.body || null}
        </HarbourNotice>
      )}

      <HarbourParentsBox parents={parents} onEdit={(p) => setChangingParent(p)} />

      <HarbourTeachersBox
        teachers={teachers}
        formerTeachers={pupil.formerTeachers || []}
        schoolName={recordSchoolName}
        formProgress={pupil.formProgress || {}}
        onAdd={() => setAddTeacherOpen(true)}
        // "Change teacher" (TDI-617) for a teacher on the school's list; a
        // teacher listed before school lists existed has no record to change
        // yet, so their pencil still opens their forms.
        onEdit={(t) => (t.schoolTeacherId ? setChangingTeacher(t) : setAllocatingTeacher(t))}
        onResend={resendTeacher}
        resendingId={resendingId}
      />

      {!pupil.yearGroup && !pupil.sen && !pupil.referral && (
        // With no year group neither pathway can be offered; say why rather
        // than leave both sections empty (staging feedback, 30 Oct).
        <HarbourNotice kind="info" title={`${firstName} cannot be added to a pathway yet`}>
          {`We cannot work out ${firstName}'s year group from the date of birth on the record, so we cannot tell which pathways ${firstName} can join. The pilot is for pupils from Reception to Year 11. Check the date of birth on ${firstName}'s record.`}
        </HarbourNotice>
      )}

      {/* ── SEND pathway section ────────────────────────────────────────── */}
      <section className="harbour-section" aria-label="SEND pathway">
        <div className="harbour-section__heading">
          <HarbourPathwayMark pathway="sen" />
          <h2 className="harbour-section__title">SEND pathway</h2>
        </div>

        {!sen && !senOffered ? (
          <div className="harbour-record-row">
            <div className="harbour-record-row__facts">
              <span className="harbour-lane__row-label">Not active</span>
              {pupil.yearGroup && (
                <span className="harbour-record-row__meta">
                  {`The SEND pathway in this pilot is for Years 1 to 6, so it is not offered for ${pupil.yearGroup}.`}
                </span>
              )}
            </div>
          </div>
        ) : !sen ? (
          <div className="harbour-record-row">
            <div className="harbour-record-row__facts">
              <span className="harbour-lane__row-label">Not active</span>
              <span className="harbour-record-row__meta">
                {`Adding ${firstName} starts the Assess stage: you confirm consent, then the school and parent or carer forms open.`}
              </span>
            </div>
            <span className="ml-auto flex-none">
              <HarbourButton kind="primary" leadingIcon="plus" onClick={() => navigate(`/rms/pupils/${pupil.id}/add-to-sen`)}>
                Add to SEND pathway
              </HarbourButton>
            </span>
          </div>
        ) : (
          <>
            {senWithdrawn && (
              <HarbourNotice kind="info" title="SEND pathway: consent withdrawn by the parent or carer" />
            )}
            <HarbourStageStrip
              pathway="sen"
              currentKey={senStopped ? (sen.stageReached || 'assess') : sen.stage}
            />
            {senStopped && !senWithdrawn ? (
              <>
                <div className="harbour-record-row">
                  <span className="harbour-record-row__label">School forms</span>
                  <HarbourStatusChip status="stoppedConsentWithdrawn" label={sen.stopReason === 'consent_withdrawn' ? 'Stopped: consent withdrawn' : 'Stopped'} />
                </div>
                <div className="harbour-record-row">
                  <span className="harbour-record-row__label">Parent or carer forms</span>
                  <HarbourStatusChip status="stoppedConsentWithdrawn" label={sen.stopReason === 'consent_withdrawn' ? 'Stopped: consent withdrawn' : 'Stopped'} />
                </div>
              </>
            ) : sen.reportAvailable ? (
              <>
                {/* Block · Plan: the profile row and its tick-off steps in one
                    card, per the design (TDI-53 section 30). */}
                <div className="harbour-plan-block">
                  <div className="harbour-record-row harbour-plan-block__head">
                    <span className="harbour-record-row__label" style={{ textDecoration: 'underline' }}>{`${firstName}'s profile`}</span>
                    <HarbourStatusChip status={planStatus} tone="sen" />
                    <span className="ml-auto flex-none flex items-center gap-4">
                      <span className="relative" ref={downloadMenuRef}>
                        <HarbourButton
                          kind="secondary"
                          leadingIcon="download"
                          trailingIcon="chevron-down"
                          aria-haspopup="menu"
                          aria-expanded={downloadMenuOpen}
                          onClick={() => setDownloadMenuOpen((open) => !open)}
                        >
                          Download
                        </HarbourButton>
                        {downloadMenuOpen && (
                          <div className="harbour-menu harbour-download-menu" role="menu" aria-label="Download">
                            <button
                              type="button"
                              role="menuitem"
                              className="harbour-menu__item harbour-download-menu__item"
                              onClick={() => openPrintView(`/rms/patient/${pupil.id}/hfp/print`)}
                            >
                              <HarbourIcon name="file-pdf" size={28} />
                              Strengths and needs profile
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="harbour-menu__item harbour-download-menu__item"
                              onClick={() => openPrintView(`/rms/pupils/${pupil.id}/answers/print`)}
                            >
                              <HarbourIcon name="file-pdf" size={28} />
                              Your answers to the school forms
                            </button>
                          </div>
                        )}
                      </span>
                      <HarbourButton kind="secondary" trailingIcon="arrow-right" onClick={() => navigate(`/rms/patient/${pupil.id}/hfp`)}>
                        View strengths and needs profile
                      </HarbourButton>
                    </span>
                  </div>
                  <div className="harbour-plan-divider" aria-hidden="true" />
                  <div className="harbour-plan-block__steps" aria-label="Plan steps">
                    {SEN_PLAN_STEPS.map((step) => (
                      <HarbourTickRow
                        key={step.action}
                        label={step.label}
                        ticked={Boolean(senPlan[step.action])}
                        // A step cannot be cleared while a later one is ticked.
                        disabled={senWithdrawn || busyAction === 'planAction'
                          || SEN_PLAN_STEPS.some((later) => later.after === step.action && senPlan[later.action])}
                        onToggle={(ticked) => togglePlanAction(step, ticked)}
                        detail={step.action === 'meetingScheduled' && formatTickDate(senPlan[step.action])
                          ? { label: 'Date', value: formatTickDate(senPlan[step.action]) }
                          : null}
                      />
                    ))}
                  </div>
                </div>
                {/* The forms blocks are hidden at Plan (Tom's 19 Oct sweep);
                    the school's answers stay in the Download menu. */}
              </>
            ) : (
              <>
                <div className="harbour-record-group">
                  <div className="harbour-record-row">
                    <span className="harbour-record-row__label">School forms</span>
                    <HarbourStatusChip {...chipProps(roleFormChip(pupil.senSchoolStatus))} />
                    <span className="ml-auto flex-none">
                      {senWithdrawn ? (
                        <HarbourTextLink to={`/rms/pupils/${pupil.id}/forms/sen`}>View</HarbourTextLink>
                      ) : senSchoolToConfirm ? (
                        // The confirm step is the outstanding action, so the
                        // row keeps the filled button and says what it is for.
                        <HarbourButton
                          kind="primary"
                          trailingIcon="arrow-right"
                          onClick={() => navigate(`/rms/pupils/${pupil.id}/forms/sen`)}
                        >
                          Check and confirm
                        </HarbourButton>
                      ) : pupil.senSchoolStatus === 'completed' ? (
                        <HarbourTextLink to={`/rms/pupils/${pupil.id}/forms/sen`}>Open school forms</HarbourTextLink>
                      ) : (
                        <HarbourButton
                          kind="primary"
                          trailingIcon="arrow-right"
                          onClick={() => navigate(`/rms/pupils/${pupil.id}/forms/sen`)}
                        >
                          Open school forms
                        </HarbourButton>
                      )}
                    </span>
                  </div>
                  {/* One line per person who holds some of the school forms,
                      so the SENCO can see at a glance who is still to finish. */}
                  {senSchoolByPerson.map((row) => (
                    <FormsProgressRow
                      key={row.key}
                      label={row.label}
                      progress={{ completed: row.completed, total: row.total }}
                      onOpen={!senWithdrawn && teacherByKey(row.key)
                        ? () => setAllocatingTeacher(teacherByKey(row.key)) : null}
                    />
                  ))}
                  {!senWithdrawn && addTeacherRow}
                </div>
                <div className="harbour-record-group">
                  <div className="harbour-record-row">
                    <span className="harbour-record-row__label">Parent or carer forms</span>
                    <HarbourStatusChip {...chipProps(roleFormChip(pupil.senGuardianStatus))} />
                    <span className="ml-auto flex-none">{senWithdrawn ? null : parentFormsAction(pupil.senGuardianStatus, senGuardian)}</span>
                  </div>
                  {guardianFirstName && (
                    <FormsProgressRow label={`${guardianFirstName}'s forms`} progress={senProgress('guardian')} />
                  )}
                </div>
                {!senWithdrawn && (
                  <HarbourNotice kind="info">
                    {senSchoolToConfirm && senGuardianConfirmed
                      ? `You have finished your forms, and ${guardianFirstName || 'the parent or carer'} has confirmed theirs. Check your answers and confirm them, then ${firstName}'s profile is created.`
                      : senSchoolToConfirm
                      ? `You have finished your forms. Check your answers and confirm them, then ${firstName}'s profile is created once ${guardianFirstName || 'the parent or carer'} has confirmed theirs too.`
                      : senGuardianToConfirm
                        ? `${guardianFirstName || 'The parent or carer'} has finished their forms and still has to confirm them. ${firstName}'s profile is created once they do.`
                        : `${firstName}'s profile is created when both sets of forms are done.`}
                  </HarbourNotice>
                )}
              </>
            )}
          </>
        )}
        {/* Stop the SEND pathway (TDI-805, R-056): offered while the pathway
            is live, before and after the profile. Teachers never see the
            record, and a stopped or withdrawn pathway has nothing to stop. */}
        {sen && !senStopped && (
          <div className="harbour-record-row">
            <HarbourButton
              kind="secondary"
              onClick={() => { setStopSenReason(null); setStopSenError(null); setStopSenOpen(true); }}
            >
              Stop the SEND pathway
            </HarbourButton>
          </div>
        )}
      </section>

      {/* ── Referral pathway section ────────────────────────────────────── */}
      <section className="harbour-section" aria-label="Referral pathway">
        <div className="harbour-section__heading">
          <HarbourPathwayMark pathway="referral" />
          <h2 className="harbour-section__title">Referral pathway</h2>
        </div>

        {!referral ? (
          <div className="harbour-record-row">
            <span className="harbour-lane__row-label">Not active</span>
            <span className="ml-auto flex-none">
              {isHarbourPupil && (
                <HarbourButton
                  kind={referralAddKind}
                  leadingIcon="plus"
                  onClick={() => navigate(`/rms/pupils/${pupil.id}/refer`)}
                >
                  Add to referral pathway
                </HarbourButton>
              )}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* One box per referral (TDI-691, 4223:305958): its strip, the
                nominated parent or carer, the receipt line while it waits,
                and once the letter is out, the date it was sent and the
                letter row. The strip's current stage is the status, so a
                chip appears only for what the strip cannot show: a stop. */}
            {referral.pathways.map((p) => {
              const label = PATHWAY_LABELS[p.key] || p.label;
              const hasLetter = letterRows.some((l) => l.key === p.key);
              const sentOn = hasLetter ? formatLongDate(letterSentAt(pupil.patient, p.key)) : null;
              return (
                <HarbourReferralBlock
                  key={p.key}
                  name={`${label} referral`}
                  stageKey={referralBoardStage(p)}
                  status={isStoppedPathwayState(p.status) ? (
                    <HarbourStatusChip status={p.status} tone="referral" label={REFERRAL_STAGE_LABELS[p.status]} />
                  ) : null}
                  facts={[
                    informantName(parents) && `Nominated Parent or Carer: ${informantName(parents)}`,
                    p.status === 'sentWaitingForReview'
                      && `${FAMILY_SERVICE_NAME} has received the referral for ${firstName}. If you have questions, contact the team by email at ${SERVICE_REFERRALS_CONTACT.email} or by phone ${SERVICE_REFERRALS_CONTACT.phone}.`,
                    sentOn && `Letter sent ${sentOn}`,
                  ]}
                  letter={hasLetter ? {
                    label: `${label} referral letter`,
                    actions: (
                      <>
                        <HarbourButton
                          kind="secondary"
                          leadingIcon="download"
                          aria-label={`Download ${label} referral letter`}
                          busy={busyAction === `download:${p.key}`}
                          onClick={() => downloadLetter(p.key)}
                        >
                          Download
                        </HarbourButton>
                        <HarbourButton
                          // One filled button per view: the letter takes it
                          // only when the SEND section is not already holding
                          // it and there is a single letter to open.
                          kind={letterRows.length === 1 && !senOwnsFilled ? 'primary' : 'secondary'}
                          trailingIcon="arrow-right"
                          onClick={() => navigate(`/rms/pupils/${pupil.id}/letter/${p.key}`)}
                        >
                          {viewLetterLabel(p.key)}
                        </HarbourButton>
                      </>
                    ),
                  } : null}
                />
              );
            })}
            {/* Once the referral has gone (or been stopped) the forms are
                nothing the school can act on, but the school can always get
                back to its own submitted answers (TDI-799, SOP A.4): the
                record dropped the forms rows at send, which left a
                referral-only pupil with no way back. */}
            {referralSent && (
              <div className="harbour-record-row">
                <span className="harbour-record-row__label">Your answers to the school forms</span>
                <span className="ml-auto flex-none">
                  <HarbourButton
                    kind="secondary"
                    leadingIcon="download"
                    onClick={() => openPrintView(`/rms/pupils/${pupil.id}/answers/print`)}
                  >
                    Download your answers
                  </HarbourButton>
                </span>
              </div>
            )}
            {/* One set of forms, however many pathways the referral runs on.
                Once the referral has gone they are no longer anything the
                school can act on, so the record stops listing them. */}
            {!referralSent && (
              <div className="flex flex-col gap-2">
                <>
                  <div className="harbour-record-row">
                    <span className="harbour-record-row__label">School forms</span>
                    <HarbourStatusChip {...chipProps(roleFormChip(pupil.schoolStatus))} />
                    <span className="ml-auto flex-none">
                      {pupil.schoolStatus !== 'completed' && (
                        <HarbourButton
                          kind={senOwnsFilled ? 'secondary' : 'primary'}
                          trailingIcon="arrow-right"
                          onClick={() => navigate(`/rms/pupils/${pupil.id}/forms/referral`)}
                        >
                          Open school forms
                        </HarbourButton>
                      )}
                      {referralSchoolToConfirm && (
                        <HarbourButton
                          kind={senOwnsFilled ? 'secondary' : 'primary'}
                          trailingIcon="arrow-right"
                          onClick={() => navigate(`/rms/pupils/${pupil.id}/forms/referral`)}
                        >
                          Check and confirm
                        </HarbourButton>
                      )}
                      {pupil.schoolStatus === 'completed' && !referralSchoolToConfirm && (
                        <HarbourTextLink to={`/rms/pupils/${pupil.id}/forms/referral`}>Open school forms</HarbourTextLink>
                      )}
                    </span>
                  </div>
                  {referralSchoolByPerson.map((row) => (
                    <FormsProgressRow
                      key={row.key}
                      label={row.label}
                      progress={{ completed: row.completed, total: row.total }}
                      onOpen={teacherByKey(row.key) ? () => setAllocatingTeacher(teacherByKey(row.key)) : null}
                    />
                  ))}
                  {addTeacherRow}
                  <div className="harbour-record-row">
                    <span className="harbour-record-row__label">Parent or carer forms</span>
                    <HarbourStatusChip {...chipProps(roleFormChip(pupil.guardianStatus))} />
                    <span className="ml-auto flex-none">{parentFormsAction(pupil.guardianStatus, referralGuardian)}</span>
                  </div>
                  {(referralSchoolToConfirm || referralGuardianToConfirm) && (
                    <HarbourNotice kind="info">
                      {(() => {
                        const parentName = (referralGuardian && referralGuardian.firstName) || 'the parent or carer';
                        if (referralSchoolToConfirm && referralGuardianConfirmed) {
                          return `You have finished your forms, and ${parentName} has confirmed theirs. Check your answers and confirm them, and the referral is sent.`;
                        }
                        if (referralSchoolToConfirm) {
                          return `You have finished your forms. Check your answers and confirm them. The referral is sent once ${parentName} has confirmed theirs too.`;
                        }
                        return `${parentName.charAt(0).toUpperCase()}${parentName.slice(1)} has finished their forms and still has to confirm them. The referral is sent once they do.`;
                      })()}
                    </HarbourNotice>
                  )}
                </>
              </div>
            )}
            {/* The school's documents (TDI-578, D-04): inside the referral,
                under the forms, and kept read-only once it is sent. Only
                the school's own files ever come back from the server. */}
            <ReferralDocuments
              orgId={orgId}
              pupil={pupil}
              onAdd={() => navigate(`/rms/pupils/${pupil.id}/documents/add`)}
              onNotice={setDocumentNotice}
            />
          </div>
        )}
      </section>

      {stopSenOpen && (
        <HarbourModal
          title={`Stop the SEND pathway for ${firstName}?`}
          confirmLabel="Stop the SEND pathway"
          confirmKind="warning"
          busy={busyAction === 'stopSen'}
          onConfirm={confirmStopSen}
          onClose={() => setStopSenOpen(false)}
        >
          <div className="flex flex-col gap-4">
            <p className="harbour-body-16" style={{ fontWeight: 700 }}>Why is the pathway stopping?</p>
            <HarbourOptionGroup
              ariaLabel="Why is the pathway stopping?"
              tone="sen"
              options={[
                { value: 'consent_withdrawn', label: 'The family has told us they want to leave' },
                { value: 'other', label: 'Other' },
              ]}
              value={stopSenReason}
              onChange={(value) => { setStopSenReason(value); setStopSenError(null); }}
            />
            {stopSenError && (
              <HarbourNotice kind="error" title={stopSenError} />
            )}
            {/* The consequences, spelled out (R-056, and R-047's promise):
                wording to cross Jasmine's comprehension gate. */}
            <p className="harbour-body-16">
              {`The SEND pathway stops where it is, and the hospital team can no longer see ${firstName}’s profile. The family can give their permission again in the Digital Hub, and the pathway will carry on from where it stopped.`}
            </p>
            <p className="harbour-body-16">
              {`Stopping the pathway won’t stop the school supporting ${firstName}.`}
            </p>
          </div>
        </HarbourModal>
      )}

      {toast && (
        <HarbourToast title={toast.title} onClose={() => setToast(null)}>
          {toast.body}
        </HarbourToast>
      )}

      {addTeacherOpen && (
        <HarbourAddTeacherModal
          orgId={orgId}
          pupil={pupil}
          onClose={() => setAddTeacherOpen(false)}
          onAdded={teacherAdded}
        />
      )}

      {allocatingTeacher && (
        // TDI-618: which school forms this teacher fills in, saved and
        // emailed once on "Save and email {first name}".
        <HarbourTeacherFormsModal
          orgId={orgId}
          pupil={pupil}
          teacher={teachers.find((t) => (t.participantId || t.id) === (allocatingTeacher.participantId || allocatingTeacher.id)) || allocatingTeacher}
          teachers={teachers}
          groups={teacherGroups}
          onClose={() => setAllocatingTeacher(null)}
          onSaved={formsSaved}
        />
      )}

      {changingTeacher && (
        <HarbourChangeTeacherModal
          orgId={orgId}
          pupil={pupil}
          teacher={changingTeacher}
          schoolName={changingTeacher.schoolTeacherSchoolName || recordSchoolName}
          onClose={() => setChangingTeacher(null)}
          onSaved={(saved) => {
            const name = teacherFullName(saved) || teacherFullName(changingTeacher);
            setChangingTeacher(null);
            setToast({ title: `${name}'s details are saved.` });
            if (onRefresh) onRefresh();
          }}
          onRemove={() => { setRemovingTeacher(changingTeacher); setChangingTeacher(null); }}
          onLeft={(res) => {
            const name = teacherFullName(changingTeacher);
            const school = changingTeacher.schoolTeacherSchoolName || recordSchoolName || 'your school';
            setChangingTeacher(null);
            setToast({
              title: `${name} has been taken off ${school}'s list.`,
              body: formsBackLine((res && res.unfinishedCount) || 0),
            });
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {changingParent && (
        <HarbourChangeParentModal
          orgId={orgId}
          pupil={pupil}
          parent={changingParent}
          onClose={() => setChangingParent(null)}
          onSaved={({ name }) => {
            setChangingParent(null);
            setToast({ title: `${name}'s details are saved.` });
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {removingTeacher && (
        <HarbourModal
          title={`Remove ${[removingTeacher.firstName, removingTeacher.lastName].filter(Boolean).join(' ')}?`}
          confirmLabel="Remove"
          busy={removeBusy}
          onConfirm={removeTeacher}
          onClose={() => setRemovingTeacher(null)}
        >
          <p className="harbour-body-16">
            {`They will no longer be listed on ${firstName}'s record, and any forms allocated to them close at once. Answers they have already saved are kept.`}
          </p>
        </HarbourModal>
      )}
    </HarbourFlyout>
  );
}

// Block · Documents on the record (TDI-578, D-04 frames 02 to 06): the list,
// "Add a document", and Remove with its confirm step. Remove is destructive,
// so its confirm uses the semantic red button (design rules), not plum.
function ReferralDocuments({ orgId, pupil, onAdd, onNotice }) {
  const { summary, loading, error, reload, remove } = useMyReferralDocuments({
    orgId,
    patientRecordId: pupil.id,
    enabled: Boolean(pupil.referral),
  });
  const removal = useDocumentRemoval({ remove, reload, onNotice });
  const { removing } = removal;
  return (
    <>
      <HarbourDocumentsBlock
        summary={summary}
        loading={loading}
        error={error}
        onRetry={reload}
        onAdd={onAdd}
        onRemove={removal.setRemoving}
      />
      {removing && (
        <HarbourModal
          title={REMOVE_TITLE}
          confirmLabel={REMOVE_CONFIRM}
          confirmKind="warning"
          busy={removal.busy}
          onConfirm={removal.confirm}
          onClose={removal.cancel}
        >
          <p className="harbour-body-16">{removeBody(removing.description)}</p>
        </HarbourModal>
      )}
    </>
  );
}

// "Your forms · 7 of 7 done": the count under a form row (frame 4223:94627).
// A teacher's line opens "{name}'s forms" (TDI-618): allocation lives inside
// the pathway (design T-15).
function FormsProgressRow({ label, progress, onOpen = null }) {
  if (!progress || !progress.total) return null;
  const { completed, total } = progress;
  const glyph = completed >= total ? 'ring-tick' : completed > 0 ? 'ring-half' : 'ring-empty';
  return (
    <div className="harbour-record-row harbour-record-row--sub">
      <span className="harbour-record-row__label harbour-record-row__label--sub">
        {onOpen ? <HarbourTextLink onClick={onOpen}>{label}</HarbourTextLink> : label}
      </span>
      <span className="harbour-record-row__count">
        <HarbourGlyph kind={glyph} size={20} />
        {`${completed} of ${total} done`}
      </span>
    </div>
  );
}

// "19 October 2026" from a recorded step ({ at: Timestamp | Date | ISO }).
const FAMILY_REMINDER_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

function toReminderDate(at) {
  if (!at) return null;
  const d = typeof at.toDate === 'function' ? at.toDate() : new Date(at);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTickDate(step) {
  const at = step && step.at;
  if (!at) return null;
  const d = typeof at.toDate === 'function' ? at.toDate() : new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function chipProps(chip) {
  return { status: chip.status, label: chip.label, tone: chip.tone || 'neutral' };
}

function informantName(parents) {
  const inf = parents.find((p) => p.isInformant);
  if (!inf) return null;
  return [inf.firstName, inf.lastName].filter(Boolean).join(' ') || inf.email;
}
